import React, { useEffect } from "react";
import {
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Box,
  Stack,
  Checkbox,
  FormControlLabel,
  Avatar,
  Divider,
  alpha,
  useTheme,
  Tooltip,
  IconButton,
} from "@mui/material";
import {
  ExpandMore,
  PriorityHigh,
  Layers,
  Palette,
  GridView,
  Opacity,
  Grass,
  Science,
  Straighten,
  AssignmentTurnedIn,
  WbSunny,
  Texture,
  Info,
  Add,
  Remove,
} from "@mui/icons-material";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckBoxIcon from "@mui/icons-material/CheckBox";

// Ortak UI Bileşenlerini İçe Aktarıyoruz
import {
  SubSectionHeader,
  RenderInput,
  RenderSelect,
  RenderMultiSelect,
  ModernInputWrapper,
} from "../components/common/ModernUI";

// AKK Derinlik ve Bünye Hesaplayıcıları
import { getAkkDepthCode, getAkkTextureCode } from "../utils/akkUtils";

import {
  HORIZON_TEMPLATE,
  SINIR_BELIRGINLIK_OPTIONS,
  SINIR_SEKIL_OPTIONS,
  TEKSTUR_OPTIONS,
  STRUKTUR_DAYANIKLILIK,
  STRUKTUR_BUYUKLUK,
  STRUKTUR_TIP,
  KIVAM_KURU,
  KIVAM_NEMLI,
  KIVAM_YAS_YAPISKANLIK,
  KIVAM_YAS_PLASTIKLIK,
  KIREC_OPTIONS,
  TASLILIK_HORIZON_OPTIONS,
  KOK_MIKTAR_OPTIONS,
  KOK_KALINLIK_OPTIONS,
  MUNSELL_HUE_OPTIONS,
  MUNSELL_VALUE_CHROMA_OPTIONS,
} from "../constants/horizonConstants";

// --- AKILLI GEÇİRGENLİK KUTUSU BİLEŞENİ ---
const PermeabilityCheckbox = ({ formData, setFormData, index }) => {
  const theme = useTheme();
  if (index !== 0) return null;

  const bolge = formData.bolge || "Marmara";
  const cetvelNo = ["İç Anadolu", "Doğu Anadolu", "Güneydoğu Anadolu"].includes(
    bolge,
  )
    ? 2
    : 1;

  const rawDepth = getAkkDepthCode(formData.tesirli_derinlik);
  let tableDepth = "D";
  if (rawDepth === "3") tableDepth = "OD";
  if (rawDepth === "4") tableDepth = "S";
  if (rawDepth === "5") tableDepth = "ÇS";

  const topHorizon = formData.horizons?.[0] || {};
  const rawTex = getAkkTextureCode(topHorizon.tekstur, topHorizon.kirec);
  let tableTex = "O";
  if (rawTex.startsWith("H") || rawTex.startsWith("F")) tableTex = "İ";
  else if (rawTex === "M" || rawTex.startsWith("S")) tableTex = "O";
  else if (rawTex.startsWith("L") || rawTex === "V") tableTex = "K";

  let showHizli = false;
  let showCokYavas = false;

  if (cetvelNo === 1) {
    if (tableTex === "K" && ["D", "ÇS", "S"].includes(tableDepth))
      showHizli = true;
    if (tableTex === "İ" && tableDepth === "D") showCokYavas = true;
  } else if (cetvelNo === 2) {
    if (tableTex === "K" && ["D", "S"].includes(tableDepth)) showHizli = true;
    if (tableTex === "İ" && tableDepth === "D") showCokYavas = true;
  }

  if (!showHizli && !showCokYavas) return null;

  return (
    <Box
      sx={{
        mt: 1,
        p: 1,
        bgcolor: alpha(theme.palette.warning.main, 0.05),
        borderRadius: 2,
        border: "1px dashed",
        borderColor: "warning.main",
        display: "flex",
        alignItems: "center",
      }}
    >
      {showCokYavas && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.is_gecirgenlik_cok_yavas}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_gecirgenlik_cok_yavas: e.target.checked,
                }))
              }
              color="warning"
              size="small"
            />
          }
          label={
            <Typography
              variant="caption"
              fontWeight="bold"
              color="warning.dark"
            >
              Geçirgenlik "Çok Yavaş" mı?
            </Typography>
          }
          sx={{ m: 0 }}
        />
      )}
      {showHizli && (
        <FormControlLabel
          control={
            <Checkbox
              checked={!!formData.is_gecirgenlik_hizli}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  is_gecirgenlik_hizli: e.target.checked,
                }))
              }
              color="warning"
              size="small"
            />
          }
          label={
            <Typography
              variant="caption"
              fontWeight="bold"
              color="warning.dark"
            >
              Geçirgenlik "Çok Hızlı" mı?
            </Typography>
          }
          sx={{ m: 0 }}
        />
      )}
    </Box>
  );
};

const getSampleSummary = (horizons) => {
  if (!horizons || horizons.length === 0) return "-";
  const takenSamples = horizons.filter((h) => !h.ornek_alinamadi);
  if (takenSamples.length === 0) return "Hiç örnek alınmadı";
  return takenSamples
    .map((h) => `(${h.derinlik_min}-${h.derinlik_max || "?"})`)
    .join("");
};

// --- ANA BİLEŞEN ---
const HorizonTab = ({ formData, setFormData }) => {
  const theme = useTheme();

  const handleHorizonCountChange = (e) => {
    let count = parseInt(e.target.value);
    if (isNaN(count)) count = 0;
    if (count > 15) return;

    setFormData((prev) => {
      const currentHorizons = prev.horizons || [];
      let newHorizons = [...currentHorizons];

      if (count > currentHorizons.length) {
        const addedCount = count - currentHorizons.length;
        for (let i = 0; i < addedCount; i++) {
          const lastHorizon = newHorizons[newHorizons.length - 1];
          const startDepth = lastHorizon ? lastHorizon.derinlik_max || 0 : 0;
          newHorizons.push({
            ...HORIZON_TEMPLATE,
            no: currentHorizons.length + 1 + i,
            derinlik_min: startDepth,
            derinlik_max: "",
            kok_sacak_miktar: "Yok",
            kok_sacak_kalinlik: "Yok",
            kok_kazik_miktar: "Yok",
            kok_kazik_kalinlik: "Yok",
          });
        }
      } else {
        newHorizons = newHorizons.slice(0, count);
      }
      return { ...prev, horizon_sayisi: count, horizons: newHorizons };
    });
  };

  const handleHorizonChange = (index, field, value) => {
    setFormData((prev) => {
      const newHorizons = [...prev.horizons];
      let updatedHorizon = { ...newHorizons[index], [field]: value };

      if (field === "kok_sacak_miktar") {
        if (value === "Yok") updatedHorizon.kok_sacak_kalinlik = "Yok";
        else if (updatedHorizon.kok_sacak_kalinlik === "Yok")
          updatedHorizon.kok_sacak_kalinlik = "";
      }
      if (field === "kok_kazik_miktar") {
        if (value === "Yok") updatedHorizon.kok_kazik_kalinlik = "Yok";
        else if (updatedHorizon.kok_kazik_kalinlik === "Yok")
          updatedHorizon.kok_kazik_kalinlik = "";
      }
      if (field === "derinlik_max") {
        let checkedValue = value;
        if (parseFloat(checkedValue) > 200) checkedValue = "200";
        updatedHorizon.derinlik_max = checkedValue;

        const nextIndex = index + 1;
        if (nextIndex < newHorizons.length) {
          newHorizons[nextIndex] = {
            ...newHorizons[nextIndex],
            derinlik_min: checkedValue,
          };
        }
      }

      newHorizons[index] = updatedHorizon;
      return { ...prev, horizons: newHorizons };
    });
  };

  const handleMaxDepthBlur = (index) => {
    const horizon = formData.horizons[index];
    const minVal = parseFloat(horizon.derinlik_min) || 0;
    const maxVal = parseFloat(horizon.derinlik_max);

    if (
      horizon.derinlik_max !== "" &&
      (isNaN(maxVal) || maxVal < minVal + 5 || maxVal > 200)
    ) {
      handleHorizonChange(index, "derinlik_max", "");
    }
  };

  useEffect(() => {
    if (formData.horizons && formData.horizons.length > 0) {
      setFormData((prev) => {
        const newH = [...prev.horizons];
        newH[0].derinlik_min = 0;
        if (!newH[0].kok_sacak_miktar) newH[0].kok_sacak_miktar = "Yok";
        if (!newH[0].kok_sacak_kalinlik) newH[0].kok_sacak_kalinlik = "Yok";
        if (!newH[0].kok_kazik_miktar) newH[0].kok_kazik_miktar = "Yok";
        if (!newH[0].kok_kazik_kalinlik) newH[0].kok_kazik_kalinlik = "Yok";
        return { ...prev, horizons: newH };
      });
    }
  }, [setFormData]);

  return (
    <Box
      sx={{ width: "100%", display: "flex", flexDirection: "column", gap: 3 }}
    >
      {/* ÜST BİLGİ KUTUSU */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 3,
          p: 2,
          bgcolor: "#e3f2fd",
          borderRadius: 3,
          border: "1px solid #90caf9",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Layers color="primary" fontSize="large" />
          <Box>
            <Typography variant="subtitle2" color="primary" fontWeight="bold">
              KATMAN SAYISI
            </Typography>

            {/* EKLENEN ARTI / EKSİ BUTONLARI */}
            <Stack
              direction="row"
              alignItems="center"
              spacing={1}
              sx={{ mt: 0.5 }}
            >
              <IconButton
                onClick={() =>
                  handleHorizonCountChange({
                    target: { value: formData.horizons.length - 1 },
                  })
                }
                disabled={formData.horizons.length <= 1}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.error.main, 0.1),
                  color: "error.main",
                  "&:hover": { bgcolor: alpha(theme.palette.error.main, 0.2) },
                }}
              >
                <Remove fontSize="small" />
              </IconButton>

              <Typography
                fontWeight="bold"
                sx={{ minWidth: 24, textAlign: "center" }}
              >
                {formData.horizons.length}
              </Typography>

              <IconButton
                onClick={() =>
                  handleHorizonCountChange({
                    target: { value: formData.horizons.length + 1 },
                  })
                }
                disabled={formData.horizons.length >= 15}
                size="small"
                sx={{
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main",
                  "&:hover": {
                    bgcolor: alpha(theme.palette.primary.main, 0.2),
                  },
                }}
              >
                <Add fontSize="small" />
              </IconButton>
            </Stack>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            bgcolor: "#fff",
            p: 1.5,
            borderRadius: 2,
            border: "1px dashed #90caf9",
            minWidth: "200px",
          }}
        >
          <AssignmentTurnedIn color="success" />
          <Box>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight="bold"
              display="block"
            >
              ALINAN ÖRNEKLER
            </Typography>
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              color="#2e7d32"
              sx={{ fontFamily: "monospace" }}
            >
              {getSampleSummary(formData.horizons)}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* HORIZON LİSTESİ */}
      <Stack spacing={2}>
        {formData.horizons &&
          formData.horizons.map((horizon, index) => {
            const minV = parseFloat(horizon.derinlik_min) || 0;
            const maxV = parseFloat(horizon.derinlik_max);
            let errorMessage = "";
            if (horizon.derinlik_max !== "") {
              if (isNaN(maxV)) errorMessage = "Geçersiz sayı";
              else if (maxV <= minV) errorMessage = "Hata";
              else if (maxV < minV + 5) errorMessage = "Min 5cm";
              else if (maxV > 200) errorMessage = "Max 200cm";
            }
            const hasError = errorMessage !== "";
            const bgColor = horizon.ornek_alinamadi
              ? "#f0f0f0"
              : hasError
                ? "#ffebee"
                : "#fafafa";
            const opacity = horizon.ornek_alinamadi ? 0.9 : 1;

            return (
              <Accordion
                key={index}
                defaultExpanded={index === 0}
                disableGutters
                elevation={0}
                sx={{
                  border: "1px solid #eaecf0",
                  borderRadius: "16px !important",
                  overflow: "hidden",
                  "&:before": { display: "none" },
                  transition: "all 0.2s",
                  "&:hover": { boxShadow: "0 4px 12px rgba(0,0,0,0.03)" },
                  bgcolor: bgColor,
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMore />}
                  sx={{
                    borderBottom: "1px solid #f0f0f0",
                    px: 3,
                    py: 1,
                    opacity: opacity,
                  }}
                >
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={2}
                    width="100%"
                  >
                    <Avatar
                      variant="rounded"
                      sx={{
                        width: 32,
                        height: 32,
                        fontSize: "0.9rem",
                        bgcolor: theme.palette.primary.main,
                        fontWeight: "bold",
                      }}
                    >
                      {index + 1}
                    </Avatar>
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography fontWeight="bold">
                        {" "}
                        {horizon.sembol
                          ? `Horizon ${horizon.sembol}`
                          : "YENİ HORİZON"}{" "}
                        {horizon.ornek_alinamadi && (
                          <span style={{ color: "#d32f2f", marginLeft: 8 }}>
                            (ÖRNEK YOK)
                          </span>
                        )}{" "}
                      </Typography>
                    </Box>
                    <Chip
                      label={`${horizon.derinlik_min} - ${horizon.derinlik_max || "?"} cm`}
                      size="small"
                      variant="outlined"
                      icon={<Straighten fontSize="small" />}
                    />
                    {hasError && (
                      <Chip
                        label={errorMessage}
                        color="error"
                        size="small"
                        variant="filled"
                        icon={<PriorityHigh />}
                        sx={{ fontWeight: "bold" }}
                      />
                    )}
                  </Stack>
                </AccordionSummary>
                <AccordionDetails
                  sx={{ p: 4, bgcolor: "#fff", opacity: opacity }}
                >
                  {/* ÖRNEK ALINMADI CHECKBOX (1. Katman Hariç) */}
                  {index !== 0 && (
                    <Box sx={{ mb: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={horizon.ornek_alinamadi || false}
                            onChange={(e) =>
                              handleHorizonChange(
                                index,
                                "ornek_alinamadi",
                                e.target.checked,
                              )
                            }
                            color={
                              horizon.ornek_alinamadi ? "error" : "success"
                            }
                            icon={<CheckBoxOutlineBlankIcon />}
                            checkedIcon={<CheckBoxIcon />}
                          />
                        }
                        label={
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 800,
                              color: horizon.ornek_alinamadi
                                ? "error.main"
                                : "success.main",
                            }}
                          >
                            {horizon.ornek_alinamadi
                              ? "BU KATMANDAN ÖRNEK ALINMADI"
                              : "ÖRNEK ALINDI"}
                          </Typography>
                        }
                        sx={{
                          width: "100%",
                          m: 0,
                          p: 1.5,
                          borderRadius: "10px",
                          border: "2px solid",
                          borderColor: horizon.ornek_alinamadi
                            ? "error.main"
                            : "success.light",
                          bgcolor: horizon.ornek_alinamadi
                            ? alpha(theme.palette.error.main, 0.05)
                            : alpha(theme.palette.success.main, 0.02),
                          transition: "all 0.2s",
                          cursor: "pointer",
                          "&:hover": {
                            bgcolor: horizon.ornek_alinamadi
                              ? alpha(theme.palette.error.main, 0.08)
                              : alpha(theme.palette.success.main, 0.05),
                          },
                        }}
                      />
                    </Box>
                  )}

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 2fr" },
                      gap: 3,
                      mb: 4,
                      p: 2,
                      bgcolor: "#f8f9fa",
                      borderRadius: "12px",
                      border: "1px dashed #e0e0e0",
                    }}
                  >
                    <RenderInput
                      name="sembol"
                      label="Sembol"
                      value={horizon.sembol}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                      placeholder="Ap"
                    />
                    <RenderInput
                      name="derinlik_min"
                      label="Min (cm)"
                      type="number"
                      value={index === 0 ? "0" : horizon.derinlik_min}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                      disabled
                      iconNode={<Straighten fontSize="small" />}
                    />
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <Box
                        sx={{ display: "flex", gap: 1, alignItems: "center" }}
                      >
                        <Box sx={{ flexGrow: 1 }}>
                          <RenderInput
                            name="derinlik_max"
                            label="Max (cm)"
                            type="number"
                            value={horizon.derinlik_max}
                            disabled={horizon.derinlik_arti}
                            error={hasError}
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                            onBlur={() => handleMaxDepthBlur(index)}
                            iconNode={<Straighten fontSize="small" />}
                          />
                        </Box>
                        {index === formData.horizons.length - 1 && (
                          <Tooltip title="Derinlik sonu ölçülemedi / Ana kaya">
                            <FormControlLabel
                              control={
                                <Checkbox
                                  size="small"
                                  checked={horizon.derinlik_arti || false}
                                  onChange={(e) => {
                                    const isChecked = e.target.checked;
                                    handleHorizonChange(
                                      index,
                                      "derinlik_arti",
                                      isChecked,
                                    );
                                    if (isChecked)
                                      handleHorizonChange(
                                        index,
                                        "derinlik_max",
                                        200,
                                      );
                                  }}
                                  sx={{
                                    color: "primary.main",
                                    "&.Mui-checked": {
                                      color: "secondary.main",
                                    },
                                  }}
                                />
                              }
                              label={
                                <Typography
                                  variant="caption"
                                  sx={{
                                    fontWeight: "bold",
                                    color: "text.secondary",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  Bitiş Belirsiz
                                </Typography>
                              }
                              sx={{
                                m: 0,
                                pl: 1,
                                pr: 1,
                                border: "1px solid #e0e0e0",
                                borderRadius: "8px",
                                bgcolor: horizon.derinlik_arti
                                  ? "#e3f2fd"
                                  : "transparent",
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ mt: 0.5, fontStyle: "italic", ml: 1 }}
                      >
                        * Bitiş derinliği
                      </Typography>
                    </Box>
                    <RenderSelect
                      name="sinir_belirginlik"
                      label="Sınır Belirginliği"
                      value={horizon.sinir_belirginlik}
                      options={SINIR_BELIRGINLIK_OPTIONS}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderSelect
                      name="sinir_sekil"
                      label="Sınır Şekli"
                      value={horizon.sinir_sekil}
                      options={SINIR_SEKIL_OPTIONS}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                  </Box>

                  <SubSectionHeader
                    title="Fiziksel Analiz & Renk"
                    iconNode={<Palette />}
                    color="#795548"
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 1fr" },
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    <Box sx={{ display: "flex", flexDirection: "column" }}>
                      <RenderSelect
                        name="tekstur"
                        label="Tekstür"
                        value={horizon.tekstur}
                        options={TEKSTUR_OPTIONS}
                        iconNode={<Texture fontSize="small" />}
                        onChange={(name, val) =>
                          handleHorizonChange(index, name, val)
                        }
                      />
                      <PermeabilityCheckbox
                        formData={formData}
                        setFormData={setFormData}
                        index={index}
                      />
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid #efebe9",
                        borderRadius: "10px",
                        bgcolor: "#fffbfb",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="brown"
                        sx={{ mb: 1, display: "block" }}
                      >
                        KURU RENK (Munsell)
                      </Typography>
                      <Stack spacing={1}>
                        <RenderSelect
                          name="renk_sinif_kuru"
                          label="Sınıf (Hue)"
                          value={horizon.renk_sinif_kuru}
                          options={MUNSELL_HUE_OPTIONS}
                          iconNode={
                            <WbSunny
                              fontSize="small"
                              sx={{ color: "orange" }}
                            />
                          }
                          onChange={(name, val) =>
                            handleHorizonChange(index, name, val)
                          }
                        />
                        <RenderSelect
                          name="renk_kod_kuru"
                          label="Kod (Val/Chr)"
                          value={horizon.renk_kod_kuru}
                          options={MUNSELL_VALUE_CHROMA_OPTIONS}
                          onChange={(name, val) =>
                            handleHorizonChange(index, name, val)
                          }
                        />
                      </Stack>
                    </Box>
                    <Box
                      sx={{
                        p: 1.5,
                        border: "1px solid #e0f7fa",
                        borderRadius: "10px",
                        bgcolor: "#f1f8e9",
                      }}
                    >
                      <Typography
                        variant="caption"
                        fontWeight="bold"
                        color="primary"
                        sx={{ mb: 1, display: "block" }}
                      >
                        YAŞ RENK (Munsell)
                      </Typography>
                      <Stack spacing={1}>
                        <RenderSelect
                          name="renk_sinif_yas"
                          label="Sınıf (Hue)"
                          value={horizon.renk_sinif_yas}
                          options={MUNSELL_HUE_OPTIONS}
                          iconNode={
                            <Opacity fontSize="small" sx={{ color: "blue" }} />
                          }
                          onChange={(name, val) =>
                            handleHorizonChange(index, name, val)
                          }
                        />
                        <RenderSelect
                          name="renk_kod_yas"
                          label="Kod (Val/Chr)"
                          value={horizon.renk_kod_yas}
                          options={MUNSELL_VALUE_CHROMA_OPTIONS}
                          onChange={(name, val) =>
                            handleHorizonChange(index, name, val)
                          }
                        />
                      </Stack>
                    </Box>
                  </Box>

                  <SubSectionHeader
                    title="Strüktür & Kıvam"
                    iconNode={<GridView />}
                    color="#f57c00"
                  />
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr 1fr",
                      },
                      gap: 2,
                      mb: 4,
                    }}
                  >
                    <RenderSelect
                      name="struktur_dayaniklilik"
                      label="Dayanıklılık"
                      value={horizon.struktur_dayaniklilik}
                      options={STRUKTUR_DAYANIKLILIK}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderSelect
                      name="struktur_buyukluk"
                      label="Büyüklük"
                      value={horizon.struktur_buyukluk}
                      options={STRUKTUR_BUYUKLUK}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderMultiSelect
                      name="struktur_tip"
                      label="Tip"
                      value={horizon.struktur_tip}
                      options={STRUKTUR_TIP}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderSelect
                      name="kivam_kuru"
                      label="Kuru Kıvam"
                      value={horizon.kivam_kuru}
                      options={KIVAM_KURU}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderSelect
                      name="kivam_nemli"
                      label="Nemli Kıvam"
                      value={horizon.kivam_nemli}
                      options={KIVAM_NEMLI}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderSelect
                      name="kivam_yas_yapiskanlik"
                      label="Y. Yapışkanlık"
                      value={horizon.kivam_yas_yapiskanlik}
                      options={KIVAM_YAS_YAPISKANLIK}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                    <RenderSelect
                      name="kivam_yas_plastiklik"
                      label="Y. Plastiklik"
                      value={horizon.kivam_yas_plastiklik}
                      options={KIVAM_YAS_PLASTIKLIK}
                      onChange={(name, val) =>
                        handleHorizonChange(index, name, val)
                      }
                    />
                  </Box>

                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
                      gap: 4,
                    }}
                  >
                    <Box>
                      <SubSectionHeader
                        title="Kök Dağılımı"
                        iconNode={<Grass />}
                        color="#388e3c"
                      />
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          <RenderSelect
                            name="kok_sacak_miktar"
                            label="Saçak Miktar"
                            value={horizon.kok_sacak_miktar}
                            options={KOK_MIKTAR_OPTIONS}
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                          />
                          <RenderSelect
                            name="kok_sacak_kalinlik"
                            label="Saçak Kalınlık"
                            value={horizon.kok_sacak_kalinlik}
                            options={KOK_KALINLIK_OPTIONS}
                            disabled={
                              !horizon.kok_sacak_miktar ||
                              horizon.kok_sacak_miktar === "Yok"
                            }
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          <RenderSelect
                            name="kok_kazik_miktar"
                            label="Kazık Miktar"
                            value={horizon.kok_kazik_miktar}
                            options={KOK_MIKTAR_OPTIONS}
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                          />
                          <RenderSelect
                            name="kok_kazik_kalinlik"
                            label="Kazık Kalınlık"
                            value={horizon.kok_kazik_kalinlik}
                            options={KOK_KALINLIK_OPTIONS}
                            disabled={
                              !horizon.kok_kazik_miktar ||
                              horizon.kok_kazik_miktar === "Yok"
                            }
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                          />
                        </Box>
                      </Stack>
                    </Box>
                    <Box>
                      <SubSectionHeader
                        title="Kimyasal & Özel"
                        iconNode={<Science />}
                        color="#7b1fa2"
                      />
                      <Stack spacing={2}>
                        <Box
                          sx={{
                            display: "grid",
                            gridTemplateColumns: "1fr 1fr",
                            gap: 2,
                          }}
                        >
                          <RenderSelect
                            name="kirec"
                            label="Kireç"
                            value={horizon.kirec}
                            options={KIREC_OPTIONS}
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                          />
                          <RenderSelect
                            name="taslilik"
                            label="Taşlılık"
                            value={horizon.taslilik}
                            options={TASLILIK_HORIZON_OPTIONS}
                            onChange={(name, val) =>
                              handleHorizonChange(index, name, val)
                            }
                          />
                        </Box>
                        <RenderInput
                          name="ozel_gorunum"
                          label="Özel Görünümler (Leke, Jips vb.)"
                          value={horizon.ozel_gorunum}
                          onChange={(name, val) =>
                            handleHorizonChange(index, name, val)
                          }
                          multiline
                          rows={2}
                          iconNode={<Info fontSize="small" />}
                        />
                      </Stack>
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })}
      </Stack>
    </Box>
  );
};

export default React.memo(HorizonTab);
